import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { AddCircleOutlined, EditOutlined, MoreVertOutlined, PauseCircleOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { Row } from "@tanstack/react-table"
import type { ReactElement } from "react"
import React, { useRef, useState } from "react"

import type { Product } from "@/graphql/codegen/graphql"
import { ComputedProductStatus } from "@/graphql/codegen/graphql"
import { useProductActionsStore } from "@/screens/Companies/products/stores/useProductsActions"
import { ProductActions } from "@/screens/Companies/products/types/schema"

interface ProductRowActionsProps {
  row: Row<Product>
  onToggleProductStatus: () => void
}

const MENU_ICON_PROPS = {
  fontVariationSettings: '"wght" 300',
}

const ProductRowActions = ({ row, onToggleProductStatus }: ProductRowActionsProps): ReactElement => {
  const navigate = useNavigate()
  const setProductAtAction = useProductActionsStore((state) => state.setProductAtAction)
  const onAction = useProductActionsStore((state) => state.onAction)

  const product = row.original
  const [open, setOpen] = useState<boolean>(false)
  const anchorElRef = useRef<HTMLButtonElement>(null)

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()

    setProductAtAction(product)
    setOpen(true)
  }
  const handleClose = () => {
    setProductAtAction(null)
    setOpen(false)
  }

  const handleToggleProductStatus = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await onAction(
      product.computedProductStatus === ComputedProductStatus.Active
        ? ProductActions.DEACTIVATE_PRODUCT
        : ProductActions.ACTIVATE_PRODUCT
    )

    onToggleProductStatus()
    handleClose()
  }

  const handleViewProductDetails = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate({ to: `/app/product/${product.productId}/edit/information` })
  }

  return (
    <>
      <IconButton aria-label='expand row' size='small' onClick={handleOpen} ref={anchorElRef}>
        <MoreVertOutlined />
      </IconButton>

      <Menu
        anchorEl={anchorElRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            className: "w-[250px] ",
          },
        }}
      >
        <MenuItem onClick={handleViewProductDetails} className={"cursor-pointer hover:bg-primary-100"}>
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          <ListItemText primary='View Product Details' />
        </MenuItem>

        <MenuItem onClick={handleToggleProductStatus}>
          <ListItemIcon>
            {product.computedProductStatus === ComputedProductStatus.Active ? (
              <PauseCircleOutlined sx={MENU_ICON_PROPS} />
            ) : (
              <AddCircleOutlined sx={MENU_ICON_PROPS} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={product.computedProductStatus === ComputedProductStatus.Active ? "Deactivate" : "Activate"}
          />
        </MenuItem>
      </Menu>
    </>
  )
}

export default ProductRowActions
